'use client'

import { ChangeEvent, FormEvent, ReactNode, useEffect, useMemo, useRef, useState } from 'react'
import { compact } from 'lodash'
import { v4 as uuid } from 'uuid'
import { Cell, Divider, Icon, InputBar, Spacer, Text } from '@vinted/web-ui'
import { ArrowLeft24, Search16 } from '@vinted/monochrome-icons'

import { useTracking } from '@marketplace-web/observability/event-tracker-data'
import { useTranslate } from '@marketplace-web/i18n/i18n-feature'
import {
  flattenDynamicFilterOptions,
  transformDynamicFilterDefaultOptions,
  DynamicFilterOptionModel,
  LinksModel,
  SearchTranslationsModel,
  OptionType,
  SelectionType,
  TranslationModel,
} from '@marketplace-web/catalog/catalog-data'
import {
  useCatalogContext,
  DynamicFilterReducerAction,
  clickEvent,
} from '@marketplace-web/catalog/catalog-page-data'
import {
  userClickDynamicFilterOptionEvent,
  userViewDynamicFilterOptionEvent,
} from '@marketplace-web/catalog/catalog-filters-data'

import useFetchDynamicFilterSearch from '../../../../../../hooks/useFetchDynamicFilterSearch'
import SelectableItemList, {
  RenderItemProps,
  SelectableListItem,
} from '../../../SelectableItemList'
import DynamicFilterCell from '../../common/DynamicFilterCell'
import FilterCell from '../../../FilterCell'
import FilterModal from '../../../FilterModal'
import FilterModalNavigation from '../../../FilterModalNavigation'
import FilterLoader from '../../common/FilterLoader'
import FilterNoResults from '../../common/FilterNoResults'

type ListOptionItem = SelectableListItem<DynamicFilterOptionModel>

type Props = {
  type: string
  title: string
  selectionType: SelectionType
  isSelectionHighlighted?: boolean
  options: Array<DynamicFilterOptionModel>
  selectedIds: Array<number>
  fromScreen: string
  isFilterCellVisible: boolean
  rootOptionType: OptionType
  searchTranslations?: SearchTranslationsModel
  isHidden?: boolean
  links?: Array<LinksModel>
  totalItemsCount?: number | null
  totalCountMax?: number | null
  isAsyncFacet?: boolean
  isLazy?: boolean
  isLoading?: boolean
  translations?: TranslationModel
  onListOptionSelect: (id: number) => void
  clearSelectedOptions: (type: string, ids?: Array<number>) => void
  onSubmit: () => void
  onFilterCellClick: () => void
  onFilterCellView: () => void
  onModalOpen: () => void
  onModalClose: () => void
  onItemCountRecalculate?: (filterType: string) => void
}

const SearchFilterModal = ({
  type,
  title,
  selectionType,
  isSelectionHighlighted,
  options,
  selectedIds,
  fromScreen,
  isFilterCellVisible,
  rootOptionType,
  searchTranslations,
  isHidden,
  links,
  totalItemsCount,
  totalCountMax,
  isAsyncFacet,
  isLazy,
  isLoading,
  translations,
  onListOptionSelect,
  clearSelectedOptions,
  onSubmit,
  onFilterCellClick,
  onFilterCellView,
  onModalOpen,
  onModalClose,
  onItemCountRecalculate,
}: Props) => {
  const translate = useTranslate()
  const { track } = useTracking()
  const fetchFilterSearch = useFetchDynamicFilterSearch()
  const {
    dispatch,
    state: {
      items: { searchTrackingParams },
      dynamicFilters: { filterSearchQuery },
    },
  } = useCatalogContext()
  const { globalSearchSessionId, globalCatalogBrowseSessionId } = searchTrackingParams
  const searchSessionId = searchTrackingParams.sessionId
  const seenItems = useRef<Array<number>>([])
  const [query, setQuery] = useState<string | null>(null)
  const [isModalOpen, setIsModalOpen] = useState(false)
  const [highlightedSelectedIds, setHighlightedSelectedIds] = useState<Array<number>>(selectedIds)
  const [filteringSessionId, setFilteringSessionId] = useState('')
  const [showClearingLoader, setShowClearingLoader] = useState(false)
  const screen = `${'dynamic_filter'}_${type}`
  const noInputBox = !!isLazy && !query && options.length === 0
  const selectedTranslation = !!isLazy && !query ? translations : searchTranslations

  // only show clearing loader when options are empty AND we're clearing (not searching with results)
  const isClearingLoading = showClearingLoader && options.length === 0
  const isLoadingContent = isLoading || isClearingLoading

  const flattedOptions = useMemo(() => {
    return flattenDynamicFilterOptions(options)
  }, [options])

  const suffixText = useMemo(() => {
    return compact(
      selectedIds.map(selectedId => flattedOptions.find(option => option.id === selectedId)?.title),
    ).join(', ')
  }, [selectedIds, flattedOptions])

  const listItems = useMemo(() => {
    return transformDynamicFilterDefaultOptions(
      options,
      rootOptionType,
      isSelectionHighlighted,
      highlightedSelectedIds,
    )
  }, [options, rootOptionType, isSelectionHighlighted, highlightedSelectedIds])

  const closeModal = () => {
    setFilteringSessionId('')
    seenItems.current = []
    setShowClearingLoader(false)

    if (filterSearchQuery.code) {
      dispatch({
        type: DynamicFilterReducerAction.SET_FILTER_SEARCH_QUERY,
        payload: { text: null, code: null },
      })
    }

    setIsModalOpen(false)
    onModalClose()
  }

  const openModal = () => {
    setIsModalOpen(true)

    setQuery(null)
    setHighlightedSelectedIds(selectedIds)
    setShowClearingLoader(false)
    onModalOpen()
  }

  const getCurrentPosition = (id: number) =>
    flattedOptions.findIndex(option => option.id === id) + 1

  const handleBackClick = (catalogCode: string) => () => {
    if (catalogCode === OptionType.Root) closeModal()

    seenItems.current = []
    setHighlightedSelectedIds(selectedIds)
    setShowClearingLoader(false)

    if (query) setQuery('')
  }

  const handleItemClick = (id: number, position: number, itemsCount?: number) => {
    onListOptionSelect(id)

    track(
      userClickDynamicFilterOptionEvent({
        id,
        type,
        code: type,
        position,
        itemsCount,
        isSelected: !selectedIds.includes(id),
        searchText: query || '',
        filteringSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
        searchSessionId,
        fromScreen,
      }),
    )
  }

  const handleDefaultItemClick = (item: ListOptionItem) => {
    const id = Number(item.id)
    const position = getCurrentPosition(id)
    const itemsCount = item.data?.itemsCount

    handleItemClick(id, position, itemsCount)
  }

  const handleSubmitClick = () => {
    closeModal()
    onSubmit()

    track(
      clickEvent({
        screen,
        target: 'apply_filters',
      }),
    )

    if (query) setQuery('')
  }

  const clearSelectedOptionIds = (groupIds?: Array<number>) => () => {
    clearSelectedOptions(type, groupIds)
    setShowClearingLoader(false)

    if (query) setQuery('')

    track(
      clickEvent({
        target: 'clear_filters',
        screen,
      }),
    )
  }

  const handleFilterCellClick = () => {
    openModal()
    onFilterCellClick()
  }

  const getHandleItemView = (item: ListOptionItem) => (inView: boolean) => {
    if (!inView) return

    const itemId = Number(item.id)
    if (seenItems.current.includes(itemId)) return

    const position = getCurrentPosition(itemId)

    track(
      userViewDynamicFilterOptionEvent({
        id: itemId,
        type,
        code: type,
        position,
        itemsCount: item.data?.itemsCount,
        searchText: query || '',
        filteringSessionId,
        globalSearchSessionId,
        globalCatalogBrowseSessionId,
        searchSessionId,
        fromScreen,
      }),
    )

    seenItems.current = [...seenItems.current, itemId]
  }

  const handleSearchInput = (
    event: ChangeEvent<HTMLInputElement> | FormEvent<HTMLTextAreaElement>,
  ) => {
    const searchQuery = event.currentTarget.value
    setQuery(searchQuery)

    if (searchQuery) {
      setShowClearingLoader(false)
      fetchFilterSearch({ text: searchQuery, code: type })
    }

    setHighlightedSelectedIds(selectedIds)
  }

  const clearSearchInput = () => {
    setShowClearingLoader(true)
    setQuery('')
  }

  useEffect(() => {
    if (isModalOpen) setFilteringSessionId(uuid())
  }, [isModalOpen])

  useEffect(() => {
    if (query !== '') return

    // clear up the query but not the code so the backend can reset the filters
    setShowClearingLoader(true)
    fetchFilterSearch({ text: null, code: type })
  }, [query, type, fetchFilterSearch])

  useEffect(() => {
    if (filterSearchQuery.text !== null) return

    // recalculate the total items when the search query is cleared
    onItemCountRecalculate?.(type)
    setQuery(null)
    setShowClearingLoader(false)
  }, [options, filterSearchQuery.text, type, onItemCountRecalculate])

  // hide loader when options populate
  useEffect(() => {
    if (options.length > 0) {
      setShowClearingLoader(false)
    }
  }, [options])

  const renderInputBarIcon = () => (
    <div className="u-flexbox u-align-items-center">
      <Icon name={Search16} color="greyscale-level-2" />
    </div>
  )

  const renderModalNavigation =
    (currentTitle: string, catalogCode: string, groupIds?: Array<number>) => () => {
      return (
        <>
          <FilterModalNavigation
            title={currentTitle}
            icon={
              <Icon
                name={ArrowLeft24}
                color="greyscale-level-1"
                testId={`${type}-navigation-prefix-icon`}
              />
            }
            prefixAriaLabel={translate('catalog.filters.a11y.actions.back')}
            suffixText={!noInputBox && translate('catalog.filters.dynamic.clear')}
            onPrefixClick={handleBackClick(catalogCode)}
            onSuffixClick={clearSelectedOptionIds(groupIds)}
          />
          <Divider />
          <Spacer />
          <div className="brand-filter__navigation-input">
            {!isLoadingContent && !noInputBox && (
              <InputBar
                name={`${type}_filter_search`}
                placeholder={searchTranslations?.placeholder || ''}
                value={query ?? ''}
                icon={renderInputBarIcon()}
                onChange={handleSearchInput}
                onValueClear={clearSearchInput}
                inputAria={{
                  'aria-label': title,
                }}
                clearButtonAria={{
                  'aria-label': translate('catalog.filters.dynamic.clear'),
                }}
              />
            )}
          </div>
        </>
      )
    }

  const renderItemCell = ({
    item,
    itemElementProps,
  }: RenderItemProps<DynamicFilterOptionModel>) => (
    <DynamicFilterCell
      key={item.id}
      type={type}
      item={item}
      itemElementProps={itemElementProps}
      handleItemView={getHandleItemView(item)}
    />
  )

  const renderModal = () => {
    let content: ReactNode

    if (isLoadingContent) {
      content = <FilterLoader />
    } else if (listItems.length === 0 && query !== '') {
      content = <FilterNoResults translations={selectedTranslation} />
    } else {
      content = (
        <>
          <SelectableItemList
            name={`${type}_ids`}
            items={listItems}
            selected={selectedIds}
            onItemClick={handleDefaultItemClick}
            renderItem={renderItemCell}
            isMultiSelect={selectionType === SelectionType.Multi}
            disableButtonClicks
          />
          <Divider />
          {links?.length && (
            <Cell>
              {links.map(link => (
                <Text key={`${type}_link`} html text={link.body} as="p" />
              ))}
            </Cell>
          )}
        </>
      )
    }

    return (
      <FilterModal
        renderNavigation={renderModalNavigation(title, OptionType.Root)}
        isOpen={isModalOpen}
        totalItemsCount={totalItemsCount}
        totalCountMax={totalCountMax}
        isAsyncFacet={isAsyncFacet}
        hideSubmitButton={isLoadingContent}
        isGoBack={noInputBox}
        hideItemCount={query !== null}
        onSubmit={handleSubmitClick}
        onGoBack={handleBackClick(OptionType.Root)}
        testId={`${type}-filter-modal`}
        disableDivider
      >
        {content}
      </FilterModal>
    )
  }

  if (isHidden) return null

  return (
    <>
      <FilterCell
        title={title}
        suffixText={suffixText || translate('catalog.filters.dynamic.all')}
        suffixTextTheme={suffixText ? 'primary' : undefined}
        onClick={handleFilterCellClick}
        onView={onFilterCellView}
        visible={isFilterCellVisible}
      />
      {renderModal()}
    </>
  )
}

export default SearchFilterModal
